import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { firstAllowedPath, canOpenPath } from '../../components/admin/AdminLayout';
import { Lock, ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';
import { useWorkshopBranding } from '../../lib/useWorkshopBranding';

const formatErr = (detail) => {
  if (detail == null) return 'Login failed. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

const AdminLogin = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const workshop = useWorkshopBranding();
  // A workshop other than the founding account is signing in: show its brand
  // instead of Driveline + Unisol, whether or not it has uploaded a logo yet.
  const ownBrand = Boolean(workshop.name) && !workshop.isFounding;

  // Pick the right landing page for the logged-in user.
  // Staff users with the new "can_self_mark_attendance" perm get the
  // admin-console self-service page; otherwise they go to the legacy
  // standalone staff portal at /staff.
  const landingFor = (u) => {
    if (u?.role === 'staff') {
      if ((u.permissions || {}).can_self_mark_attendance) return '/admin/my-attendance';
      const granted = firstAllowedPath(u);
      // Nothing granted beyond the always-on self-service pages — send them
      // to their own portal, which is where clock in/out lives.
      return ['/admin/my-performance', '/admin/settings/password'].includes(granted)
        ? '/staff'
        : granted;
    }
    // Land on the first module this account can actually open, so a
    // permission-limited admin never opens on a screen missing from their menu.
    return u?.is_master_admin ? '/admin/inventory' : firstAllowedPath(u);
  };

  // A `from` carried over from a previous session must not send the new user
  // to a route they cannot open.
  const destFor = (u) => {
    const from = location.state?.from?.pathname;
    return from && canOpenPath(from, u) ? from : landingFor(u);
  };

  React.useEffect(() => {
    if (user) {
      const dest = destFor(user);
      navigate(dest, { replace: true });
    }
  }, [user, navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const loggedIn = await login(email.trim(), password);
      const dest = destFor(loggedIn);
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-scope relative min-h-screen bg-[#FAFAF8] text-slate-900 flex flex-col items-center justify-center px-5 py-10 sm:px-8 overflow-hidden">
      {/* soft brand wash — keeps the flat white from feeling empty */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 -right-32 h-[26rem] w-[26rem] rounded-full bg-red-500/[0.06] blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-48 -left-40 h-[30rem] w-[30rem] rounded-full bg-slate-900/[0.05] blur-3xl"
      />

      <div className="absolute top-5 left-5 sm:top-7 sm:left-7 z-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 text-sm transition-colors"
          data-testid="admin-login-back-link"
        >
          <ArrowLeft size={16} /> Back to site
        </Link>
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8 sm:mb-10">
          {ownBrand ? (
            <>
              {workshop.logo && (
                <img
                  src={workshop.logo}
                  alt={workshop.name}
                  className="mx-auto max-h-24 sm:max-h-28 max-w-[18rem] w-auto object-contain select-none"
                  data-testid="login-workshop-logo"
                />
              )}
              {/* Inline colour: the login page is otherwise slate-on-white, so
                  the accent has nothing to attach to via the theme override. */}
              <p
                className={`${workshop.logo ? 'mt-4 text-base' : 'text-2xl'} font-semibold`}
                style={{ color: workshop.accent || 'rgb(71 85 105)' }}
                data-testid="login-workshop-name"
              >
                {workshop.name}
              </p>
              {workshop.accent && (
                <div className="mx-auto mt-3 h-0.5 w-16 rounded-full"
                     style={{ backgroundColor: workshop.accent }}
                     data-testid="login-accent-rule" />
              )}
              <div className="mt-5 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-400">
                <span>Powered by</span>
                <img src="/brand/driveline-logo.png" alt="Driveline" className="h-4 w-auto opacity-80" />
              </div>
            </>
          ) : (
            <img
              src="/brand/driveline-logo.png"
              alt="Driveline"
              className="mx-auto w-[15rem] sm:w-[19rem] lg:w-[22rem] max-w-full h-auto select-none"
              data-testid="admin-login-logo"
            />
          )}
          <p
            className="mt-5 sm:mt-6 text-[10px] sm:text-xs uppercase tracking-[0.22em] text-slate-400 flex items-center justify-center gap-2"
            data-testid="admin-login-title"
          >
            <Lock size={12} className="text-red-500/70" />
            Internal Console
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white border border-slate-200/80 rounded-2xl p-6 sm:p-8 shadow-[0_24px_60px_-32px_rgba(15,23,42,0.35)] space-y-5"
          data-testid="admin-login-form"
        >
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-base sm:text-sm !text-slate-900 placeholder:text-slate-400 focus:outline-none focus:bg-white focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 transition-colors"
              placeholder={ownBrand ? 'you@example.com' : 'admin@unisolglobal.com'}
              autoComplete="email"
              data-testid="admin-login-email"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pr-11 text-base sm:text-sm !text-slate-900 placeholder:text-slate-400 focus:outline-none focus:bg-white focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 transition-colors"
                placeholder="••••••••"
                autoComplete="current-password"
                data-testid="admin-login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
                title={showPassword ? 'Hide password' : 'Show password'}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                data-testid="admin-login-password-toggle"
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </div>

          {error && (
            <div
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2"
              data-testid="admin-login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-slate-900 hover:bg-slate-800 active:scale-[0.99] disabled:bg-slate-400 disabled:cursor-not-allowed disabled:active:scale-100 text-white font-semibold py-3.5 rounded-xl shadow-lg shadow-slate-900/10 transition-[background-color,transform] flex items-center justify-center gap-2"
            data-testid="admin-login-submit"
          >
            {submitting ? <Loader2 className="animate-spin" size={18} /> : null}
            {submitting ? 'Signing in…' : 'Sign In'}
          </button>

          <div className="pt-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 text-xs text-slate-400 text-center sm:text-left">
            <span>Forgot? Ask the master admin to reset.</span>
            <span>Authorized internal staff only.</span>
          </div>
        </form>

        {/* The founding account's mark — hidden once we know the visitor
            belongs to a different workshop, whose own logo is above. */}
        {!ownBrand && (
        <div className="mt-9 flex flex-col items-center gap-3" data-testid="admin-login-unisol">
          <span className="h-px w-16 bg-slate-200" />
          <span className="text-[10px] uppercase tracking-[0.22em] text-slate-400">
            Built by
          </span>
          <img
            src="/brand/unisol-logo.png"
            alt="Unisol Global"
            className="h-12 sm:h-14 w-auto opacity-90"
          />
        </div>
        )}
      </div>
    </div>
  );
};

export default AdminLogin;
